import { Graphics } from '../../../vendor/pixi.min.mjs';
import { SnakeMinimap } from './SnakeMinimap.js';
import { SnakeTopBar } from './SnakeTopBar.js';
import { LevelCompleteOverlay } from '../../ui/LevelCompleteOverlay.js';
import { ScoreSubmitOverlay } from '../../ui/ScoreSubmitOverlay.js';
import { partyMode } from '../../ui/Confetti.js';
import { t } from '../../config/i18n.js';
import { SNAKE_CONFIG } from './snakeConfig.js';
import { buildPortalGeom } from './SnakeMode_Classic.js';

const {
    EXIT_WIDTH,
    BOARD_CLEAR_BONUS,
    ADVENTURE_HOUSE_W, ADVENTURE_HOUSE_H,
    ADVENTURE_HOUSE_COLOR, ADVENTURE_HOUSE_OPEN_COLOR,
    ADVENTURE_DOOR_COLOR,
} = SNAKE_CONFIG;

const WALL_THICK = 4;

/**
 * Adventure mode — exit comes from a fixed per-screen door side.
 */
export function generateExit(scene) {
    const adv = scene._adventure;
    const side = adv.currentDoorSide;

    if (!side) {
        scene._exit = null;
        return;
    }

    const { cols, rows } = scene._grid;
    const isHoriz = (side === 'top' || side === 'bottom');
    const maxPos = isHoriz ? cols : rows;
    const start = Math.floor(Math.random() * Math.max(1, maxPos - EXIT_WIDTH));
    scene._exit = { side, start, width: EXIT_WIDTH };
}

/**
 * Draws the door (the unlocked-state graphics for the exit) in adventure mode.
 *
 * Adventure mode is the only place where the "portal to the next stage"
 * actually shows up (Classic uses the always-on exit gate; FreeRoam has
 * no portal at all). So this is also where we hand the portal-suction
 * confetti its geometry: as soon as the door unlocks, the funnel emitter
 * lights up around it; as soon as it's gone (locked, transitioned to
 * next board, last screen with no exit), we clear emission so leftover
 * particles can't float over a portal that no longer exists.
 */
export function drawDoor(scene) {
    if (scene._doorGfx) {
        scene._doorGfx.clear();
        scene._doorGfx.removeChildren();
        if (scene._doorGfx.parent) scene._doorGfx.parent.removeChild(scene._doorGfx);
    }

    const adv = scene._adventure;
    if (!adv || !scene._exit || !adv.doorUnlocked) {
        // Door isn't visible — kill any active suction confetti so it
        // doesn't keep emitting at an invisible target.
        scene._portalConfetti?.setPortal(null);
        scene._portalConfetti?.clear();
        return;
    }

    if (!scene._doorGfx) scene._doorGfx = new Graphics();

    const { cellSize: cs, originX: ox, originY: oy, cols, rows } = scene._grid;
    const gridW = cols * cs;
    const gridH = rows * cs;
    const exit = scene._exit;
    const pad = 2;

    let gx, gy, gw, gh;
    switch (exit.side) {
        case 'right':
            gx = ox + gridW - pad; gy = oy + exit.start * cs;
            gw = WALL_THICK + pad * 2; gh = exit.width * cs; break;
        case 'left':
            gx = ox - WALL_THICK - pad; gy = oy + exit.start * cs;
            gw = WALL_THICK + pad * 2; gh = exit.width * cs; break;
        case 'bottom':
            gx = ox + exit.start * cs; gy = oy + gridH - pad;
            gw = exit.width * cs; gh = WALL_THICK + pad * 2; break;
        case 'top':
            gx = ox + exit.start * cs; gy = oy - WALL_THICK - pad;
            gw = exit.width * cs; gh = WALL_THICK + pad * 2; break;
    }

    const sprite = scene.game?.theme?.createSprite('snake.door', gw, gh, { fitMode: 'cover' });
    if (sprite) {
        sprite.x = gx + gw / 2;
        sprite.y = gy + gh / 2;
        scene._doorGfx.addChild(sprite);
    } else {
        scene._doorGfx.roundRect(gx, gy, gw, gh, 4);
        scene._doorGfx.fill(ADVENTURE_DOOR_COLOR);
    }
    scene._world.addChild(scene._doorGfx);

    if (scene._portalConfetti) {
        scene._portalConfetti.clear();
        // Match Classic's explicit null-then-set pattern so the
        // arrival flash fires every time the adventure door appears,
        // not just on the very first stage. `setPortal` auto-runs
        // `flashOnAppear` on null → non-null transitions.
        scene._portalConfetti.setPortal(null);
        scene._portalConfetti.setPortal(buildPortalGeom(exit, ox, oy, cs, cols, rows));
    }
}

export function createMinimap(scene) {
    const { cols, rows } = scene._grid;
    scene._minimap = new SnakeMinimap(cols, rows);
    const mapW = scene._minimap.container.width || 130;
    const mapH = scene._minimap.container.height || 110;
    // Horizontally centre against the full canvas width — the chips live on
    // the left margin and the back/help/leaderboard buttons live on the
    // right, so the middle of the bar is the empty target zone.
    //
    // Vertically: try to sit centred inside the bar, but clamp top edge to
    // >= 0 so a taller-than-bar minimap doesn't get its top clipped by the
    // canvas edge. Any surplus then gently spills into the playfield top —
    // that's acceptable since it's a clear visual readout of progress.
    const TOP_BAR_HEIGHT = SnakeTopBar.HEIGHT;
    scene._minimap.container.x = Math.round((scene.game.width - mapW) / 2);
    scene._minimap.container.y = Math.max(0, Math.round((TOP_BAR_HEIGHT - mapH) / 2));
    scene._minimap.update(0);
    scene.container.addChild(scene._minimap.container);
}

export function updateMinimap(scene) {
    if (!scene._minimap || !scene._adventure) return;
    scene._minimap.renderLive({
        currentScreen: scene._adventure.currentScreen,
        snake: scene._snake,
        obstacles: scene._obstacles,
        food: scene._food,
        perks: scene._perks,
        exit: scene._exit,
        doorUnlocked: scene._adventure.doorUnlocked,
        housePos: scene._housePos,
        houseOpen: scene._adventure.houseOpen,
    });
}

export function placeHouse(scene) {
    const { cols, rows } = scene._grid;
    const excluded = scene._buildExcludedSet();
    scene._housePos = scene._adventure.pickHousePosition(cols, rows, excluded);
    drawHouse(scene);

    const houseCells = scene._adventure.getHouseCells(scene._housePos);
    for (const c of houseCells) {
        excluded.add(`${c.col},${c.row}`);
    }
}

/**
 * Renders the house sprite/fallback. Shared between adventure (last screen) and
 * free roam (revealed on score threshold) — exported so both modes can call it.
 */
export function drawHouse(scene) {
    if (scene._houseGfx) {
        scene._houseGfx.clear();
        scene._houseGfx.removeChildren();
        if (scene._houseGfx.parent) scene._houseGfx.parent.removeChild(scene._houseGfx);
    }
    if (!scene._housePos) return;

    const { cellSize: cs, originX: ox, originY: oy } = scene._grid;
    const { col, row } = scene._housePos;
    const x = ox + col * cs;
    const y = oy + row * cs;
    const w = ADVENTURE_HOUSE_W * cs;
    const h = ADVENTURE_HOUSE_H * cs;
    const open = scene._adventure?.houseOpen;

    scene._houseGfx = new Graphics();

    const sprite = scene.game?.theme?.createSprite('snake.house', w, h, { fitMode: 'cover' });
    if (sprite) {
        sprite.x = x + w / 2;
        sprite.y = y + h / 2;
        scene._houseGfx.addChild(sprite);
        scene._world.addChild(scene._houseGfx);
        return;
    }

    scene._houseGfx.roundRect(x + 2, y + 2, w - 4, h - 4, 6);
    scene._houseGfx.fill(open ? ADVENTURE_HOUSE_OPEN_COLOR : ADVENTURE_HOUSE_COLOR);

    scene._houseGfx.rect(x + w * 0.35, y + h * 0.55, w * 0.3, h * 0.4);
    scene._houseGfx.fill(open ? 0x166534 : 0x7f1d1d);

    const peakX = x + w / 2;
    const peakY = y - cs * 0.15;
    scene._houseGfx.moveTo(x, y + 2);
    scene._houseGfx.lineTo(peakX, peakY);
    scene._houseGfx.lineTo(x + w, y + 2);
    scene._houseGfx.closePath();
    scene._houseGfx.fill(open ? 0x15803d : 0xb91c1c);

    scene._world.addChild(scene._houseGfx);
}

/**
 * Adventure board-to-board transition. Uses scene-level helpers
 * (_repositionSnakeFromEntry / _regenerateObstacles / _regeneratePickupsAndRender)
 * so the per-mode delta stays small.
 */
export function transitionBoard(scene) {
    const adv = scene._adventure;
    const OPPOSITE_SIDE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
    const entrySide = OPPOSITE_SIDE[scene._exit.side];

    adv.advanceScreen();
    scene._boardNumber = adv.currentScreen;
    scene._score += BOARD_CLEAR_BONUS;

    // Refill the per-stage life pool — Adventure's screen advance is
    // a full stage transition just like Classic's `_transitionBoard`,
    // so the lives reset rule from the snake settings applies here
    // too.
    scene._lives = scene._livesPerStage;
    if (scene._effects) scene._effects.invincible = 0;

    scene._repositionSnakeFromEntry(entrySide);

    if (scene._doorGfx) {
        scene._doorGfx.clear();
        if (scene._doorGfx.parent) scene._doorGfx.parent.removeChild(scene._doorGfx);
        scene._doorGfx = null;
    }
    // Old portal is gone; stop emitting suction confetti at its
    // stale geometry. The next door unlock on the new board will
    // re-light the emitter via `drawDoor`.
    scene._portalConfetti?.setPortal(null);
    scene._portalConfetti?.clear();

    generateExit(scene);

    if (scene._exitGfx) {
        scene._exitGfx.clear();
        if (scene._exitGfx.parent) scene._exitGfx.parent.removeChild(scene._exitGfx);
    }

    scene._regenerateObstacles();

    if (adv.isLastScreen) {
        placeHouse(scene);
    }

    scene._housePos = adv.isLastScreen ? scene._housePos : null;
    if (!adv.isLastScreen && scene._houseGfx) {
        scene._houseGfx.clear();
        if (scene._houseGfx.parent) scene._houseGfx.parent.removeChild(scene._houseGfx);
        scene._houseGfx = null;
    }

    scene._regeneratePickupsAndRender();
    scene._playBoardIntro();
}

export function showWin(scene) {
    scene._endGame();

    const adv = scene._adventure;
    const finalScore = adv.totalScore + BOARD_CLEAR_BONUS;

    partyMode();
    // Shared cross-game victory jingle on the winner screen (ducks the
    // looping ambience so it plays clean).
    scene._audio?.playFinished();

    scene._winOverlay = new LevelCompleteOverlay();
    scene._winOverlay.show(scene.game.width, scene.game.height, {
        title: t('snake.adventure_win'),
        lines: [
            t('snake.win_screens', { v: adv.currentScreen + 1 }),
            t('snake.win_food', { v: adv.totalFood }),
            t('snake.win_score', { v: finalScore }),
        ],
        score: finalScore,
        scoreLabel: t('common.total_score'),
        onContinue: () => {
            showScoreOverlay(scene, finalScore);
        },
    });
    scene.container.addChild(scene._winOverlay.container);
}

export function showScoreOverlay(scene, finalScore) {
    if (scene._winOverlay) { scene._winOverlay.destroy(); scene._winOverlay = null; }
    scene._scoreOverlay = new ScoreSubmitOverlay();
    scene._scoreOverlay.show({
        game: 'snake',
        score: finalScore,
        outcome: 'win',
        meta: {
            mode: 'adventure',
            screens: scene._adventure.currentScreen + 1,
            foodEaten: scene._adventure.totalFood,
        },
        theme: scene.game.theme,
        onDone: () => {
            scene._scoreOverlay = null;
            scene.game.sceneManager.switchScene('menu');
        },
    });
}
